import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

interface RouteGuardProps {
  children: React.ReactNode;
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
  allowedRoles?: string[];
}

export const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  requiresAuth = false,
  requiresAdmin = false,
  allowedRoles,
}) => {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  // Home (/) stays the real home for RISP and SOI users — no auto-redirect away from it.

  // Redirect Thrace users to Thrace landing page if they're accessing the home page
  if (user?.role === 'thrace' && location.pathname === '/') {
    return <Navigate to="/thrace" replace />
  }

  // Redirect TFP users to Training Credits if they're accessing the home page
  if (user?.role?.toLowerCase() === 'tfp' && location.pathname === '/') {
    return <Navigate to="/training-credits" replace />
  }

  // If route requires admin access
  if (requiresAdmin) {
    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    
    // Check if user is admin (role === "risp" or id === 1 like in Vue app)
    if (user?.role !== 'risp' && user?.id !== 1) {
      throw new Error('You do not have permission to view this resource.');
    }
  }
  
  // If route requires authentication
  else if (requiresAuth) {
    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
  }

  // If route requires specific roles
  if (allowedRoles && allowedRoles.length > 0) {
    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    const normalizedRole = user?.role?.toLowerCase();
    const normalizedAllowedRoles = allowedRoles.map((role) => role.toLowerCase());

    if (!normalizedRole || !normalizedAllowedRoles.includes(normalizedRole)) {
      throw new Error('You do not have permission to view this resource.');
    }
  }

  // Route is public or user has access
  return <>{children}</>;
};

export default RouteGuard;
